import { Match, SportFilter } from "./MatchCards/types";
import { parseCricketMatchName, parseFootballTennisMatchData, extractHorseVenueName } from "./matchUtils";
import { getCountryFromFixture, countryNameToFlag as countryNameToFlagHorses } from "../Trader/Horses/utils.js";

// Pre-compiled regex patterns for location stripping (created once, reused)
export const LOCATION_PATTERNS = [
    /\s+from\s+[^-]+$/i,           // "from Harare Sports Club"
    /\s+at\s+[^-]+$/i,            // "at Wembley Stadium"
    /\s*-\s+[^-]+$/i,             // " - Location Name"
    /\s*@\s+[^-]+$/i,             // " @ Location Name"
    /\s+in\s+[^-]+$/i,            // "in London"
];

// Competition strings that are not real competition names (e.g. from malformed "Team v Team, Only" names)
const MEANINGLESS_CRICKET_COMPETITION = new Set(["only", "match"]);

/**
 * Extract and normalize competition name from a match
 * This is the single source of truth for competition extraction logic
 */
export const extractCompetition = (match: Match, sport: SportFilter): string => {
    if (!match) return "";
    
    let competition = "";
    
    if (sport === "CRICKET") {
        const parsed = parseCricketMatchName(match.name || "");
        competition = parsed.competition;
        // Strip everything after "MATCH" (case-insensitive) for cricket
        if (competition) {
            const matchIndex = competition.toLowerCase().indexOf(" match");
            if (matchIndex !== -1) {
                competition = competition.substring(0, matchIndex).trim();
            }
        }
        // If parsed value is empty or meaningless (e.g. "Only" from "Team v Team, Only"), use API competition or leave empty
        const trimmed = (competition || "").trim().toLowerCase();
        if (!trimmed || MEANINGLESS_CRICKET_COMPETITION.has(trimmed)) {
            competition = (match.competition && match.competition.trim()) || "";
        }
    } else if (sport === "HORSES") {
        // For horses, use country from meta tags instead of event names
        const country = getCountryFromFixture(match);
        competition = country || "";
        // Fallback to event name if country is not available
        if (!competition) {
            const parsed = parseFootballTennisMatchData(match);
            competition = parsed.competition || "";
            if (!competition && match.name) {
                competition = extractHorseVenueName(match.name);
            } else if (competition) {
                competition = extractHorseVenueName(competition);
            }
        }
    } else {
        const parsed = parseFootballTennisMatchData(match);
        competition = parsed.competition;
    }
    
    // Strip location/venue information from competition name
    if (competition) {
        let cleaned = competition;
        LOCATION_PATTERNS.forEach(pattern => {
            cleaned = cleaned.replace(pattern, '').trim();
        });
        competition = cleaned;
    }
    
    return competition.trim();
};

/** True if a competition label refers to the Indian Premier League (IPL). */
export const isIplCompetitionName = (competition: string | null | undefined): boolean => {
    if (!competition || typeof competition !== "string") return false;
    const c = competition.toLowerCase();
    return c.includes("indian premier league") || c.includes("ipl");
};

/** True for cricket trader/fixture matches whose competition or name indicates IPL. */
export const isIplCricketMatch = (match: Match): boolean => {
    if ((match.sport || "").toLowerCase() !== "cricket") return false;
    if (isIplCompetitionName(extractCompetition(match, "CRICKET"))) return true;
    if (isIplCompetitionName(match.competition)) return true;
    const name = (match.name || "").toLowerCase();
    return name.includes("indian premier league") || name.includes("ipl");
};

/**
 * Sort key for cricket competition tabs: IPL and World Cup first (0 and 1), then others (2).
 * Used when ordering competition filters so World Cup and IPL appear first.
 */
export const getCricketCompetitionTabOrder = (competition: string): number => { 
    if (!competition || typeof competition !== "string") return 2;
    const c = competition.toLowerCase();
    if (isIplCompetitionName(competition)) return 0;
    if (c.includes("world cup")) return 1;
    return 2;
};

/** FIFA Men's World Cup only — excludes Club World Cup, Kings World Cup Clubs, qualifiers, Women's, and U19. */
export const isFifaMenWorldCupCompetition = (competition: string): boolean => {
    if (!competition || typeof competition !== "string") return false;
    const n = competition.trim().toLowerCase().replace(/\bengland\b/g, "english");
    if (
        n.includes("club world cup") ||
        n.includes("kings world cup") ||
        n.includes("women") ||
        n.includes("u19") ||
        n.includes("u-19") ||
        n.includes("under 19") ||
        n.includes("under-19") ||
        n.includes("qualif")
    ) {
        return false;
    }
    if (n.includes("world cup") || n.includes("world championship")) return true;
    return false;
};

/** Football competitions to hide from the list (e.g. reserve/youth leagues). Display only main England Premier League. */
export const isFootballCompetitionIgnored = (competition: string): boolean => {
    if (!competition || typeof competition !== "string") return false;
    return /premier league\s*2\b/i.test(competition.trim());
};

/** Tennis: low-level circuits; match as whole words (word-boundary) so M500/W500 are not hidden. */
const TENNIS_LOW_LEVEL_KEYWORDS = [
    "challenger", "itf", "futures",
    "m15", "m25", "m50", "w15", "w25", "w50",
    "125k", "100k", "80k", "60k",
];
export const isTennisCompetitionIgnored = (competition: string): boolean => {
    if (!competition || typeof competition !== "string") return false;
    const lower = competition.trim().toLowerCase();
    return TENNIS_LOW_LEVEL_KEYWORDS.some((kw) => new RegExp(`\\b${kw}\\b`).test(lower));
};

/**
 * Map competitions to short names/aliases
 */
export const getCompetitionShortName = (competition: string, sport: SportFilter): string => {
    if (!competition) return "";
    const compLower = competition.toLowerCase();
    
    // Cricket competitions
    if (sport === "CRICKET") {
        if (compLower.includes("indian premier league") || compLower.includes("ipl")) return "IPL";
        if (compLower.includes("ashes")) return "THE ASHES";
        if (compLower.includes("big bash") || compLower.includes("bbl")) return "BBL";
        if (compLower.includes("the hundred")) return "THE HUNDRED";
        if (compLower.includes("sa20")) return "SA20";
        if (compLower.includes("caribbean premier league") || compLower.includes("cpl")) return "CPL";
        if (compLower.includes("pakistan super league") || compLower.includes("psl")) return "PSL";
        if (compLower.includes("bangladesh premier league") || compLower.includes("bpl")) return "BPL";
        if (compLower.includes("lanka premier league") || compLower.includes("lpl")) return "LPL";
        if (compLower.includes("vitality blast")) return "VITALITY BLAST";
        if (compLower.includes("women's premier league") || compLower.includes("wpl")) return "WPL";
        if (compLower.includes("women's big bash") || compLower.includes("wbbl")) return "WBBL";
        if (compLower.includes("world cup")) return "WORLD CUP";
        if (compLower.includes("t20 world cup")) return "T20 WORLD CUP";
        if (compLower.includes("champions trophy")) return "CHAMPIONS TROPHY";
    }
    
    // Football competitions
    if (sport === "FOOTBALL") {
        // Normalize "england" to "english" for consistent matching
        const normalizedComp = compLower.replace(/\bengland\b/g, "english");
        if ((normalizedComp.includes("premier league") && (normalizedComp.includes("english") || compLower.includes("england"))) ||
            (compLower.includes("premier league") && (compLower.includes("english") || compLower.includes("england")))) return "PREMIER LEAGUE";
        if (compLower.includes("la liga") || compLower.includes("laliga")) return "LA LIGA";
        if (compLower.includes("bundesliga")) return "BUNDESLIGA";
        if (compLower.includes("ligue 1") || compLower.includes("ligue1")) return "LIGUE 1";
        if (compLower.includes("serie a") || compLower.includes("seriea")) return "SERIE A";
        if (compLower.includes("champions league")) return "UCL";
        if (compLower.includes("europa league")) return "UEL";
        if (compLower.includes("world cup")) return "WORLD CUP";
        if (compLower.includes("euro") || compLower.includes("european championship")) return "EUROS";
        if (compLower.includes("fa cup")) return "FA CUP";
        if (compLower.includes("copa del rey")) return "COPA DEL REY";
        if (compLower.includes("dfb pokal") || compLower.includes("dfb-pokal")) return "DFB POKAL";
        if (compLower.includes("coupe de france")) return "COUPE DE FRANCE";
        if (compLower.includes("coppa italia")) return "COPPA ITALIA";
        if (compLower.includes("championship") && (compLower.includes("english") || compLower.includes("england"))) return "CHAMPIONSHIP";
    }
    
    // Tennis competitions
    if (sport === "TENNIS") {
        if (compLower.includes("wimbledon")) return "WIMBLEDON";
        if (compLower.includes("us open")) return "US OPEN";
        if (compLower.includes("french open") || compLower.includes("roland garros")) return "FRENCH OPEN";
        if (compLower.includes("australian open")) return "AUSTRALIAN OPEN";
        if (compLower.includes("atp finals")) return "ATP FINALS";
        if (compLower.includes("wta finals")) return "WTA FINALS";
        if (compLower.includes("davis cup")) return "DAVIS CUP";
        if (compLower.includes("indian wells")) return "INDIAN WELLS";
        if (compLower.includes("miami open")) return "MIAMI OPEN";
        if (compLower.includes("monte carlo") || compLower.includes("monte-carlo")) return "MONTE CARLO";
        if (compLower.includes("madrid open")) return "MADRID OPEN";
        if (compLower.includes("italian open") || compLower.includes("rome")) return "ITALIAN OPEN";
        if (compLower.includes("canadian open") || compLower.includes("rogers cup")) return "CANADIAN OPEN";
        if (compLower.includes("cincinnati")) return "CINCINNATI";
        if (compLower.includes("shanghai masters")) return "SHANGHAI";
        if (compLower.includes("paris masters")) return "PARIS MASTERS";
    }
    
    // Return uppercase version of original if no alias found
    return competition.toUpperCase();
};

const DEFAULT_COMPETITION_EMOJI = "🏆";
const WORLD_EMOJI = "🌍";

/**
 * Returns the flag emoji and competition name for filter tabs (emoji + full name).
 */
export const getCompetitionTabDisplay = (competition: string, sport: SportFilter): { flag: string; text: string } => {
    const flag = getCompetitionFlag(competition, sport);
    return { flag, text: competition };
};

/**
 * Map competitions to flags/emojis for filter tabs.
 * "World" in name → 🌍; football country keywords → country flags; unmatched → 🏆.
 */
export const getCompetitionFlag = (competition: string, sport: SportFilter): string => {
    if (!competition) return DEFAULT_COMPETITION_EMOJI;
    const compLower = competition.toLowerCase();

    if (compLower.includes("world")) return "🌍";

    if (sport === "HORSES") {
        return countryNameToFlagHorses(competition) || DEFAULT_COMPETITION_EMOJI;
    }

    if (sport === "CRICKET") {
        // Leagues & tournaments (specific first)
        if (compLower.includes("ipl") || compLower.includes("indian premier league")) return "🇮🇳";
        if (compLower.includes("women's premier league") || compLower.includes("wpl")) return "🇮🇳";
        if (compLower.includes("big bash") || compLower.includes("bbl")) return "🇦🇺";
        if (compLower.includes("women's big bash") || compLower.includes("wbbl")) return "🇦🇺";
        if (compLower.includes("the hundred")) return "🏴󠁧󠁢󠁥󠁮󠁧󠁿";
        if (compLower.includes("sa20")) return "🇿🇦";
        if (compLower.includes("caribbean premier league") || compLower.includes("cpl")) return "🇧🇧";
        if (compLower.includes("pakistan super league") || compLower.includes("psl")) return "🇵🇰";
        if (compLower.includes("bangladesh premier league") || compLower.includes("bpl")) return "🇧🇩";
        if (compLower.includes("international league t20") || compLower.includes("ilt20") || compLower.includes("uae")) return "🇦🇪";
        if (compLower.includes("major league cricket") || compLower.includes("mlc")) return "🇺🇸";
        if (compLower.includes("lanka premier league") || compLower.includes("lpl")) return "🇱🇰";
        if (compLower.includes("super smash")) return "🇳🇿";
        if (compLower.includes("vitality blast")) return "🏴󠁧󠁢󠁥󠁮󠁧󠁿";
        if (compLower.includes("syed mushtaq ali") || compLower.includes("vijay hazare") || compLower.includes("ranji trophy") || compLower.includes("duleep trophy") || compLower.includes("irani cup")) return "🇮🇳";
        if (compLower.includes("tamil nadu premier league") || compLower.includes("tnpl")) return "🇮🇳";
        if (compLower.includes("abu dhabi t10") || compLower.includes("emirates d10") || compLower.includes("t10")) return "🇦🇪";
        if (compLower.includes("falcons champions trophy")) return "🇦🇪";
        if (compLower.includes("ford trophy")) return "🇳🇿";
        if (compLower.includes("jagran cricket league")) return "🇮🇳";
        if (compLower.includes("sheffield")) return "🇦🇺";
        if (compLower.includes("chandra trophy") || compLower.includes("kanpur")) return "🇮🇳";
        if (compLower.includes("waterfalls elite league")) return "🇿🇼";
        if (compLower.includes("wncl") || compLower.includes("women's national cricket league")) return "🇦🇺";
        if (compLower.includes("kbn premier league")) return "🇮🇳";
        if (compLower.includes("legends cricket league")) return "🇮🇳";
        if (compLower.includes("ashes")) return "🇦🇺";
        // Country-based (tours, series, bilateral)
        if (compLower.includes("india") || compLower.includes("indian")) return "🇮🇳";
        if (compLower.includes("england") || compLower.includes("english")) return "🏴󠁧󠁢󠁥󠁮󠁧󠁿";
        if (compLower.includes("australia") || compLower.includes("australian")) return "🇦🇺";
        if (compLower.includes("pakistan")) return "🇵🇰";
        if (compLower.includes("bangladesh")) return "🇧🇩";
        if (compLower.includes("sri lanka")) return "🇱🇰";
        if (compLower.includes("new zealand")) return "🇳🇿";
        if (compLower.includes("south africa")) return "🇿🇦";
        if (compLower.includes("west indies")) return "🇧🇧";
        if (compLower.includes("afghanistan")) return "🇦🇫";
        if (compLower.includes("ireland") || compLower.includes("irish")) return "🇮🇪";
        if (compLower.includes("zimbabwe")) return "🇿🇼";
        if (compLower.includes("netherlands") || compLower.includes("dutch")) return "🇳🇱";
        if (compLower.includes("nepal")) return "🇳🇵";
        if (compLower.includes("scotland") || compLower.includes("scottish")) return "🏴󠁧󠁢󠁳󠁣󠁴󠁿";
        if (compLower.includes("denmark") || compLower.includes("danish")) return "🇩🇰";
        return DEFAULT_COMPETITION_EMOJI;
    }

    if (sport === "FOOTBALL") {
        if (compLower.includes("germany") || compLower.includes("german") || compLower.includes("bundesliga")) return "🇩🇪";
        if (compLower.includes("denmark") || compLower.includes("danish")) return "🇩🇰";
        if (compLower.includes("england") || compLower.includes("english")) return "🏴󠁧󠁢󠁥󠁮󠁧󠁿";
        if (compLower.includes("spain") || compLower.includes("spanish") || compLower.includes("la liga") || compLower.includes("laliga")) return "🇪🇸";
        if ((compLower.includes("premier league") || compLower.includes("championship")) && (compLower.includes("english") || compLower.includes("england"))) return "🏴󠁧󠁢󠁥󠁮󠁧󠁿";
        if (compLower.includes("ligue 1") || compLower.includes("ligue1") || compLower.includes("france") || compLower.includes("french")) return "🇫🇷";
        if (compLower.includes("serie a") || compLower.includes("seriea") || compLower.includes("italy") || compLower.includes("italian")) return "🇮🇹";
        if (compLower.includes("champions league") || compLower.includes("europa league") || compLower.includes("euro") || compLower.includes("european championship")) return "🇪🇺";
        // Only English/England FA Cup gets England flag; other FA Cups (e.g. Scotland) get trophy or their flag
        if (compLower.includes("fa cup") && (compLower.includes("english") || compLower.includes("england"))) return "🏴󠁧󠁢󠁥󠁮󠁧󠁿";
        return DEFAULT_COMPETITION_EMOJI;
    }

    if (sport === "TENNIS") {
        if (compLower.includes("wimbledon")) return "🏴󠁧󠁢󠁥󠁮󠁧󠁿";
        if (compLower.includes("us open")) return "🇺🇸";
        if (compLower.includes("french open") || compLower.includes("roland garros")) return "🇫🇷";
        if (compLower.includes("australian open")) return "🇦🇺";
        if (compLower.includes("atp") || compLower.includes("wta")) return "🌍";
        return DEFAULT_COMPETITION_EMOJI;
    }

    return DEFAULT_COMPETITION_EMOJI;
};
